import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Anchor,
  CheckCircle2,
  CircleSlash,
  Edit3,
  Search,
  ExternalLink,
  Filter,
  LogOut,
  Mail,
  Phone,
  Save,
  ShieldCheck,
  Sparkles,
  UserSearch,
  X
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { AdminMeDTO, MarketingContactDTO, MarketingContactEmailDiscoveryDTO } from "@tendvia/shared";
import { adminApi } from "../api";
import { Button } from "../components/Button";
import { Field } from "../components/Field";

type FilterKey = "all" | "pending_review" | "needs_research" | "approved" | "rejected";

const filters: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "pending_review", label: "Pending" },
  { key: "needs_research", label: "Research" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" }
];

export function Harbor() {
  const [me, setMe] = useState<AdminMeDTO | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void adminApi.me()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="grid min-h-dvh place-items-center bg-background px-4 text-text">
        <p className="rounded-token border border-border/60 bg-surface px-4 py-3 text-sm font-bold">Loading Harbor</p>
      </main>
    );
  }

  return me ? <HarborDashboard me={me} onLogout={() => setMe(null)} /> : <HarborLogin onAuthed={setMe} />;
}

function HarborLogin({ onAuthed }: { onAuthed: (me: AdminMeDTO) => void }) {
  const [step, setStep] = useState<"request" | "verify">("request");
  const [email, setEmail] = useState("lucian.badea@gmail.com");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function requestOtp(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const response = await adminApi.startOtp(email);
      setStep("verify");
      setMessage(response.devCode ? `Local dev code: ${response.devCode}` : "Check your email for the Harbor login code.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to start admin login.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      onAuthed(await adminApi.verifyOtp(email, code, name || undefined));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to verify admin login.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-4 py-8 text-text">
      <section className="w-full max-w-md rounded-panel border border-border/60 bg-surface p-6">
        <div className="mb-6">
          <div className="mb-4 inline-grid size-12 place-items-center rounded-token border border-border/40 bg-surface-soft text-primary">
            <Anchor className="size-5" aria-hidden="true" />
          </div>
          <p className="text-sm font-extrabold uppercase text-accent">Harbor</p>
          <h1 className="mt-2 text-[2rem] font-black leading-tight tracking-[-0.04em] text-text">Review and grow the TendVia market list.</h1>
          <p className="mt-3 max-w-sm text-sm leading-6 text-muted">Admin-only access for discovery, approval, and outreach prep.</p>
        </div>

        {step === "request" ? (
          <form className="grid gap-4" onSubmit={requestOtp}>
            <Field label="Admin email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required />
            <Button icon={<Mail className="size-4" aria-hidden="true" />} disabled={busy || !email}>
              {busy ? "Sending" : "Send admin code"}
            </Button>
          </form>
        ) : (
          <form className="grid gap-4" onSubmit={verifyOtp}>
            <Field label="Name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
            <Field label="Code" value={code} onChange={(event) => setCode(event.target.value)} inputMode="numeric" maxLength={6} autoComplete="one-time-code" required />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
              <Button icon={<ShieldCheck className="size-4" aria-hidden="true" />} disabled={busy || code.length !== 6}>
                {busy ? "Checking" : "Verify"}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setStep("request")}>Change email</Button>
            </div>
          </form>
        )}

        {message ? <p className="mt-4 rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-medium text-muted">{message}</p> : null}
      </section>
    </main>
  );
}

function HarborDashboard({ me, onLogout }: { me: AdminMeDTO; onLogout: () => void }) {
  const [contacts, setContacts] = useState<MarketingContactDTO[]>([]);
  const [filter, setFilter] = useState<FilterKey>("pending_review");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    void adminApi.marketingContacts(filter === "all" ? undefined : filter)
      .then((loaded) => {
        if (active) setContacts(loaded);
      })
      .catch((error) => {
        if (active) setMessage(error instanceof Error ? error.message : "Unable to load contacts.");
      });
    return () => {
      active = false;
    };
  }, [filter]);

  async function refresh() {
    setContacts(await adminApi.marketingContacts(filter === "all" ? undefined : filter));
  }

  async function logout() {
    await adminApi.logout();
    onLogout();
  }

  async function review(contact: MarketingContactDTO, reviewStatus: "approved" | "needs_research" | "rejected") {
    setBusyId(contact.id);
    setMessage("");
    try {
      await adminApi.reviewMarketingContact(contact.id, {
        reviewStatus,
        nextAction: reviewStatus === "approved" ? contact.nextAction : contact.nextAction ?? "Review source and contact route before outreach."
      });
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to review contact.");
    } finally {
      setBusyId(null);
    }
  }

  async function updateContact(contactId: string, input: { phone?: string | null; email?: string | null }) {
    setBusyId(contactId);
    setMessage("");
    try {
      await adminApi.updateMarketingContact(contactId, input);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to update contact.");
      throw error;
    } finally {
      setBusyId(null);
    }
  }

  async function findEmail(contactId: string) {
    setBusyId(contactId);
    setMessage("");
    try {
      return await adminApi.findMarketingContactEmail(contactId);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to search the website for email.");
      throw error;
    } finally {
      setBusyId(null);
    }
  }

  const counts = useMemo(() => ({
    total: contacts.length,
    high: contacts.filter((contact) => contact.discoveryPriority.toLowerCase() === "high").length,
    verified: contacts.filter((contact) => contact.sourceType.toLowerCase().includes("official")).length
  }), [contacts]);

  return (
    <main className="min-h-dvh bg-background text-text">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-surface/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 pb-3 pt-[calc(0.875rem+env(safe-area-inset-top))]">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-grid size-11 shrink-0 place-items-center rounded-token border border-border/40 bg-surface-soft text-primary">
              <Anchor className="size-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-lg font-extrabold leading-tight">Harbor</p>
              <p className="truncate text-xs font-bold text-muted">{me.admin.email}</p>
            </div>
          </div>
          <Button variant="secondary" icon={<LogOut className="size-4" aria-hidden="true" />} onClick={logout}>Sign out</Button>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-4 px-4 py-4 sm:py-5">
        <section className="grid gap-4 rounded-panel border border-border/50 bg-surface p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-token border border-border/40 bg-surface-soft px-3 py-1.5 text-xs font-extrabold text-primary">
                <Sparkles className="size-4" aria-hidden="true" />
                Gainesville seed list
              </div>
              <h1 className="mt-3 text-[2rem] font-black tracking-[-0.04em] text-text sm:text-[2.35rem]">Mobile detailer review queue</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-muted">Approve source-backed contacts for founder discovery, flag noisy entries for research, and keep consent separate from outreach status.</p>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:min-w-80">
              <Metric label="Loaded" value={counts.total} />
              <Metric label="High priority" value={counts.high} />
              <Metric label="Official source" value={counts.verified} />
            </div>
          </div>
        </section>

        <section className="rounded-panel border border-border/50 bg-surface p-3">
          <div className="mb-3 flex items-center gap-2 px-1 text-sm font-extrabold text-muted">
            <Filter className="size-4 text-primary" aria-hidden="true" />
            Review filter
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {filters.map((item) => (
              <button
                key={item.key}
                className={`min-h-11 rounded-token border px-3 py-2 text-sm font-extrabold transition ${
                  filter === item.key ? "border-transparent bg-text text-primary-contrast" : "border-border/70 bg-elevated text-muted hover:bg-surface-soft hover:text-text"
                }`}
                type="button"
                onClick={() => setFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </section>

        {message ? <p className="rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-bold text-muted">{message}</p> : null}

        <section className="grid gap-3">
          {contacts.length === 0 ? (
            <div className="rounded-panel border border-dashed border-border bg-surface-soft p-5 text-sm font-bold text-muted">No contacts in this filter.</div>
          ) : contacts.map((contact) => (
            <ContactCard
              key={`${contact.id}:${contact.updatedAt}`}
              contact={contact}
              busy={busyId === contact.id}
              onReview={review}
              onUpdate={updateContact}
              onFindEmail={findEmail}
            />
          ))}
        </section>
      </div>
    </main>
  );
}

function ContactCard({ contact, busy, onReview, onUpdate, onFindEmail }: {
  contact: MarketingContactDTO;
  busy: boolean;
  onReview: (contact: MarketingContactDTO, reviewStatus: "approved" | "needs_research" | "rejected") => void;
  onUpdate: (contactId: string, input: { phone?: string | null; email?: string | null }) => Promise<void>;
  onFindEmail: (contactId: string) => Promise<MarketingContactEmailDiscoveryDTO>;
}) {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(contact.phone ?? "");
  const [email, setEmail] = useState(contact.email ?? "");
  const [localMessage, setLocalMessage] = useState("");

  async function saveContact(event: FormEvent) {
    event.preventDefault();
    setLocalMessage("");
    try {
      await onUpdate(contact.id, {
        phone: phone.trim() || null,
        email: email.trim() || null
      });
      setEditing(false);
    } catch (error) {
      setLocalMessage(error instanceof Error ? error.message : "Unable to save contact.");
    }
  }

  async function findEmail() {
    setLocalMessage("");
    try {
      const result = await onFindEmail(contact.id);
      if (!result.primaryEmail) {
        setLocalMessage(`No email found. Checked ${result.searchedUrls.length} page${result.searchedUrls.length === 1 ? "" : "s"}.`);
        return;
      }
      setEditing(true);
      setEmail(result.primaryEmail);
      setLocalMessage(
        result.candidates.length > 1
          ? `Found ${result.candidates.length} candidates. Prefilled ${result.primaryEmail}.`
          : `Found ${result.primaryEmail}.`
      );
    } catch (error) {
      setLocalMessage(error instanceof Error ? error.message : "Unable to search the website for email.");
    }
  }

  return (
    <article className="rounded-panel border border-border/50 bg-surface p-4">
      <div className="grid gap-4 lg:grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-extrabold">{contact.businessName}</h2>
            <Badge label={contact.fitScore} tone={contact.discoveryPriority.toLowerCase() === "high" ? "success" : "warning"} />
            <Badge label={contact.reviewStatus.replaceAll("_", " ")} tone="info" />
          </div>
          <p className="mt-1 text-sm font-bold text-muted">{contact.city} · {contact.geoRing}</p>
          <p className="mt-3 text-sm font-medium text-muted">{contact.sourceNotes}</p>
          {editing ? (
            <form className="mt-3 grid gap-3" onSubmit={saveContact}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Phone" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="352-555-0100" />
                <Field label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="owner@business.com" />
              </div>
              {localMessage ? <p className="rounded-token border border-border/60 bg-surface-soft px-3 py-2 text-sm font-bold text-muted">{localMessage}</p> : null}
              <div className="flex flex-wrap gap-2">
                <Button icon={<Save className="size-4" aria-hidden="true" />} disabled={busy}>Save contact</Button>
                <Button
                  type="button"
                  variant="secondary"
                  icon={<X className="size-4" aria-hidden="true" />}
                  disabled={busy}
                  onClick={() => {
                    setEditing(false);
                    setPhone(contact.phone ?? "");
                    setEmail(contact.email ?? "");
                    setLocalMessage("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <Info icon={Phone} label={contact.phone ?? "No phone"} />
              <Info icon={Mail} label={contact.email ?? "No email"} />
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {contact.website ? <LinkButton href={contact.website} label="Website" /> : null}
            {contact.contactUrl ? <LinkButton href={contact.contactUrl} label="Contact" /> : null}
            <LinkButton href={contact.sourceUrl} label="Source" />
          </div>
          <div className="mt-3 rounded-token border border-border/40 bg-surface-soft p-3">
            <p className="text-xs font-extrabold uppercase text-primary">Next action</p>
            <p className="mt-1 text-sm font-medium text-muted">{contact.nextAction ?? "Review source and contact route."}</p>
          </div>
        </div>
        <div className="grid content-start gap-2 sm:grid-cols-3 lg:w-48 lg:grid-cols-1">
          <Button
            variant="secondary"
            icon={<Search className="size-4" aria-hidden="true" />}
            disabled={busy}
            onClick={findEmail}
          >
            Find email
          </Button>
          <Button
            variant="secondary"
            icon={<Edit3 className="size-4" aria-hidden="true" />}
            disabled={busy}
            onClick={() => {
              setEditing((value) => !value);
              setLocalMessage("");
            }}
          >
            {editing ? "Editing" : "Edit"}
          </Button>
          <Button icon={<CheckCircle2 className="size-4" aria-hidden="true" />} disabled={busy} onClick={() => onReview(contact, "approved")}>Approve</Button>
          <Button variant="secondary" icon={<UserSearch className="size-4" aria-hidden="true" />} disabled={busy} onClick={() => onReview(contact, "needs_research")}>Research</Button>
          <Button variant="ghost" icon={<CircleSlash className="size-4" aria-hidden="true" />} disabled={busy} onClick={() => onReview(contact, "rejected")}>Reject</Button>
        </div>
      </div>
    </article>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-token border border-border/50 bg-surface p-3">
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="mt-1 text-xs font-bold text-muted">{label}</p>
    </div>
  );
}

function Badge({ label, tone }: { label: string; tone: "success" | "warning" | "info" }) {
  const classes = {
    success: "border-success/20 bg-success/10 text-success",
    warning: "border-warning/20 bg-warning/10 text-warning",
    info: "border-info/20 bg-info/10 text-info"
  };
  return <span className={`rounded-token border px-2.5 py-1 text-xs font-extrabold capitalize ${classes[tone]}`}>{label}</span>;
}

function Info({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2 rounded-token border border-border/40 bg-elevated px-3 py-2 font-bold text-muted">
      <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}

function LinkButton({ href, label }: { href: string; label: string }) {
  return (
    <a className="inline-flex min-h-10 items-center gap-2 rounded-token border border-border/70 bg-elevated px-3 py-2 text-sm font-extrabold text-text transition hover:bg-surface-warm" href={href} target="_blank" rel="noreferrer">
      <ExternalLink className="size-4 text-primary" aria-hidden="true" />
      {label}
    </a>
  );
}
